import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, BriefcaseBusiness, Check, File, FileImage, FileText, FolderInput, Globe2,
  Settings2, Sparkles, Trash2, UploadCloud, UserRoundPlus, WandSparkles, X,
} from 'lucide-react';
import { api } from '../api.js';
import { Badge, Button, Callout, EmptyState, IconButton, PageHeader, SectionHeader } from '../components/ui.jsx';
import { candidateNameFromFile, fileTypeFromName } from '../utils.js';

const ACCEPT = '.pdf,.doc,.docx,.txt,.rtf,.png,.jpg,.jpeg,.webp,.tif,.tiff';

const fileIcon = (file) => file.type.startsWith('image/') ? FileImage : file.name.toLowerCase().endsWith('.pdf') ? FileText : File;

function formatSize(bytes) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function StagedFiles({ files, onRemove }) {
  const groups = files.reduce((result, file) => {
    const name = candidateNameFromFile(file.name) || '待系统识别';
    result[name] ||= [];
    result[name].push(file);
    return result;
  }, {});
  return (
    <div className="staged-groups">
      {Object.entries(groups).map(([name, items]) => (
        <section key={name}>
          <div className="staged-group-title"><span className="candidate-match-avatar">{name.slice(0, 1).toUpperCase()}</span><div><strong>{name}</strong><small>候选人识别预览 · {items.length} 份简历</small></div><Badge tone={name === '待系统识别' ? 'warning' : 'success'}>{name === '待系统识别' ? '需确认姓名' : '可自动归档'}</Badge></div>
          {items.map((file) => {
            const Icon = fileIcon(file);
            return <div className="staged-file" key={`${file.name}-${file.lastModified}`}><span><Icon size={17} /></span><div><strong>{file.name}</strong><small>{fileTypeFromName(file.name)} · {formatSize(file.size)}</small></div><IconButton icon={X} label="移除文件" onClick={() => onRemove(file)} /></div>;
          })}
        </section>
      ))}
    </div>
  );
}

export default function ImportPage({ state, selectedJobId, refresh, notify, navigate }) {
  const jobs = state?.jobs || [];
  const [jobId, setJobId] = useState(selectedJobId || jobs.find((item) => item.status === 'active')?.id || jobs[0]?.id || '');
  const [files, setFiles] = useState([]);
  const [autoProcess, setAutoProcess] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [assignments, setAssignments] = useState({});
  const inputRef = useRef(null);
  const job = jobs.find((item) => item.id === jobId);
  const unmatched = (state?.unmatchedAssets || []).filter((asset) => asset.jobId === jobId);
  const assessmentModelReady = Boolean(state?.system?.aiReadiness?.assessment ?? state?.system?.aiReadiness?.scoring);

  useEffect(() => {
    if (!assessmentModelReady) setAutoProcess(false);
  }, [assessmentModelReady]);

  const totals = useMemo(() => ({
    size: files.reduce((sum, file) => sum + file.size, 0),
    candidates: new Set(files.map((file) => candidateNameFromFile(file.name)).filter(Boolean)).size,
  }), [files]);

  const addFiles = (list) => {
    const next = [...list].filter((file) => /\.(pdf|docx?|txt|rtf|png|jpe?g|webp|tiff?)$/i.test(file.name));
    setFiles((current) => {
      const keys = new Set(current.map((file) => `${file.name}-${file.size}-${file.lastModified}`));
      return [...current, ...next.filter((file) => !keys.has(`${file.name}-${file.size}-${file.lastModified}`))];
    });
  };

  const submit = async () => {
    if (!files.length) return notify('请先添加简历文件', 'danger');
    const form = new FormData();
    if (!jobId) return notify('请先选择归档岗位', 'danger');
    if (autoProcess && !assessmentModelReady) return notify('请先配置可用的 AI 审核 Provider 和模型', 'danger');
    form.append('jobId', jobId);
    form.append('autoProcess', String(autoProcess));
    files.forEach((file) => form.append('files', file));
    setSubmitting(true);
    try {
      const imported = await api.importCandidates(form);
      setResult(imported);
      notify(`${imported.autoProcess ? '已导入并启动处理' : '已归档'} ${imported.matchedCount} 份资产${imported.unmatchedCount ? `，${imported.unmatchedCount} 份待匹配` : ''}`);
      setFiles([]);
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setSubmitting(false); }
  };

  const assign = async (asset) => {
    const value = assignments[asset.id]?.trim();
    if (!value) return notify('请输入候选人姓名', 'danger');
    if (autoProcess && !assessmentModelReady) return notify('请先配置可用的 AI 审核 Provider 和模型', 'danger');
    try {
      await api.assignAsset(asset.id, { candidateName: value, autoProcess });
      notify(`${asset.originalName} 已归档到 ${value}${autoProcess ? '，并已进入评估队列' : ''}`);
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
  };

  return (
    <div className="page import-page">
      <PageHeader
        eyebrow={job?.title}
        title="批量导入"
        description="上传后按文件名识别候选人，并可立即进入 AI 评估队列"
        actions={<><label className="page-job-select"><BriefcaseBusiness size={16} /><select value={jobId} onChange={(event) => setJobId(event.target.value)} aria-label="归档岗位">{jobs.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}</select></label><Button icon={FolderInput} onClick={() => navigate('candidates', jobId)}>查看候选人库</Button></>}
      />

      <div className="import-layout">
        <section className="import-main">
          <div
            className={`drop-zone ${dragging ? 'dragging' : ''}`}
            onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
            onDrop={(event) => { event.preventDefault(); setDragging(false); addFiles(event.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
          >
            <input ref={inputRef} type="file" accept={ACCEPT} multiple hidden onChange={(event) => addFiles(event.target.files)} />
            <span className="drop-icon"><UploadCloud size={27} /></span>
            <h2>批量上传简历</h2>
            <p>PDF、DOCX、TXT、PNG、JPG、WEBP、TIFF · 单文件不超过 250 MB</p>
            <Button variant="secondary">选择文件</Button>
          </div>

          {files.length > 0 && (
            <section className="staging-area">
              <SectionHeader title="归档预览" description={`${files.length} 份文件 · 识别到约 ${totals.candidates} 位候选人 · ${formatSize(totals.size)}`} action={<button className="text-action danger" onClick={() => setFiles([])}><Trash2 size={14} />清空</button>} />
              <StagedFiles files={files} onRemove={(file) => setFiles((current) => current.filter((item) => item !== file))} />
            </section>
          )}

          {!assessmentModelReady && <Callout tone="warning" title="AI 处理暂不可用" action={<Button size="sm" icon={Settings2} onClick={() => navigate('models')}>配置 AI Provider</Button>}>请先在“模型与处理”中为审核模型选择已启用的 Provider 和 Model，并配置 API Base URL 与 API Key。当前仍可导入并归档简历，但不会启动 AI 处理。</Callout>}

          <div className="import-submit-bar">
            <label className={`toggle-row ${assessmentModelReady ? '' : 'disabled'}`}><input type="checkbox" checked={autoProcess} disabled={!assessmentModelReady} onChange={(event) => setAutoProcess(event.target.checked)} /><span><i /></span><div><strong>导入后立即开始证据处理与 AI 评估</strong><small>{assessmentModelReady ? '会读取文件，并将必要内容发送给已配置的 API Provider' : '完成 AI Provider 配置后才可启用'}</small></div></label>
            <Button variant="primary" icon={WandSparkles} loading={submitting} onClick={submit}>确认导入</Button>
          </div>

          {result && <Callout tone={result.unmatchedCount ? 'warning' : 'success'} title={result.autoProcess ? '导入完成，已进入处理队列' : '导入完成，尚未开始评估'}>识别 {result.candidates.length} 位候选人，自动归档 {result.matchedCount} 份简历，{result.unmatchedCount} 份文件名无法识别、需要人工确认姓名。{result.autoProcess ? '系统将依次执行文件解析、信息提取和模型评分。' : '可在候选人页点击“开始评估未完成项”。'}</Callout>}
        </section>

        <aside className="import-side">
          <section className="import-rule-panel">
            <span className="side-panel-icon"><Sparkles size={20} /></span>
            <h3>自动归档规则</h3>
            <div className="rule-flow"><span>文件名</span><i /><span>姓名识别</span><i /><span>候选人档案</span></div>
            <ul><li><Check size={14} />识别“简历 / Resume / CV”</li><li><Check size={14} />自动去除文件名中的岗位后缀</li><li><Check size={14} />相同姓名简历自动合并</li><li><Check size={14} />无法识别时提示人工确认</li></ul>
          </section>
          <section className="privacy-panel"><ShieldCopy /><div><strong>本地文件存储</strong><p>文件保存在本机应用数据目录，不进入 OneDrive 项目依赖目录。</p></div></section>
          <section className="import-stats-panel"><div><FileText size={16} /><span>简历文件</span><strong>{files.length}</strong></div><div><UserRoundPlus size={16} /><span>预计候选人</span><strong>{totals.candidates}</strong></div></section>
        </aside>
      </div>

      <section className="unmatched-section">
        <SectionHeader title="人工确认姓名" description="仅文件名无法识别候选人时进入这里，确认后可直接继续评估" action={unmatched.length > 0 && <Badge tone="warning">{unmatched.length} 份待处理</Badge>} />
        {unmatched.length === 0 ? <EmptyState icon={Check} title="没有待匹配资产" description="所有已导入文件都已归档到候选人。" /> : (
          <div className="unmatched-table">{unmatched.map((asset) => (
            <div key={asset.id}><span className="asset-icon"><AlertTriangle size={17} /></span><div><strong>{asset.originalName}</strong><small>{assetKindLabelSafe(asset.kind)} · 文件名识别置信度 {Math.round(asset.matchConfidence * 100)}%</small></div><label><input value={assignments[asset.id] || ''} onChange={(event) => setAssignments((current) => ({ ...current, [asset.id]: event.target.value }))} placeholder="输入候选人姓名" /></label><Button size="sm" icon={autoProcess ? WandSparkles : undefined} onClick={() => assign(asset)}>{autoProcess ? '归档并评估' : '确认归档'}</Button></div>
          ))}</div>
        )}
      </section>
    </div>
  );
}

function ShieldCopy() {
  return <span className="privacy-icon"><Globe2 size={19} /></span>;
}

function assetKindLabelSafe(kind) {
  return ({ resume: '简历', resume_image: '图片简历' })[kind] || '简历文件';
}
